import { faBomb, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridColDef } from '@mui/x-data-grid-pro'
import { Ref } from 'react'
import styled from 'styled-components'

import { Button, DialogWrapper } from '@/components'
import { Flex } from '@/components/Layout'
import { TranslationHelper } from '@/context/Locale/Locale'
import {
	DomainComboDto,
	StereotypeDto,
	TechnicalColumnDto,
} from '@/endpoints/models'
import { TechnicalImpactModal } from '@/pages/User/pages/Settings/pages/TechnicalColumns/components/TechnicalColumnImpactModal'
import { TechnicalRemoveModal } from '@/pages/User/pages/Settings/pages/TechnicalColumns/components/TechnicalColumnRemoveModal'

import { CheckBoxFormField, Form } from '../../../../../../components/UberForm'
import { gridColumnsAudit } from '../../utils'
import { TechColumnModal } from './components/TechColumnModal'

export const createColumnProperties =
	(ref: Ref<Form<TechnicalColumnDto>>) =>
	(
		t: TranslationHelper,
		handleSave: () => void,
		domains: DomainComboDto[] | undefined,
		stereotypes: StereotypeDto[],
	): GridColDef<TechnicalColumnDto>[] => {
		const getDomain = (id: number | undefined) =>
			domains?.find((dom) => dom.id === id)

		return [
			{
				field: 'name',
				headerName: t('TABLE_COLUMNS_NAME'),
				minWidth: 140,
				flex: 1,
			},
			{
				field: 'code',
				headerName: t('TABLE_COLUMNS_CODE'),
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'domainId',
				headerName: t('TABLE_COLUMNS_DOMAIN'),
				minWidth: 140,
				flex: 1,
				valueGetter: (_value, row) => getDomain(row.domainId)?.name ?? '',
			},
			{
				field: 'notNull',
				headerName: t('TECH_COLUMN_NOT_NULL'),
				width: 110,
				sortable: false,
				filterable: false,
				align: 'center',
				headerAlign: 'left',
				renderCell: (params) => (
					<div className="flex justify-center items-center w-full h-full">
						<CheckboxSimpleTable
							disabled
							name="notNull"
							initialValue={getDomain(params.row.domainId)?.notNullFlag}
						/>
					</div>
				),
			},
			{
				field: 'stereotypeIds',
				headerName: t('TABLE_COLUMNS_STEREOTYPE'),
				minWidth: 160,
				flex: 1,
				valueGetter: (_value, row) =>
					row.stereotypeIds
						?.map(
							(stereotypeId) =>
								stereotypes.find((stereo) => stereo.id === stereotypeId)?.name,
						)
						.filter(Boolean)
						.join(', ') ?? '',
			},
			...gridColumnsAudit<TechnicalColumnDto>(t),
			{
				field: 'actions',
				headerName: '',
				width: 140,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: (params) => (
					<Flex>
						<DialogWrapper
							dialog={(opened, onClose) =>
								opened && (
									<TechnicalImpactModal
										technicalColumn={params.row}
										open={opened}
										onClose={onClose}
									/>
								)
							}
						>
							{(onClick) => (
								<Button size="sm" schema="transparent" onClick={onClick}>
									<FontAwesomeIcon
										title={t('SHOW_IMPACTED_TABLES')}
										icon={faBomb}
									/>
								</Button>
							)}
						</DialogWrapper>
						<DialogWrapper
							dialog={(opened, onClose) =>
								opened && (
									<TechColumnModal
										stereotypes={stereotypes}
										techCol={params.row}
										onSave={handleSave}
										onClose={onClose}
										ref={ref}
									/>
								)
							}
						>
							{(onClick) => (
								<Button size="sm" schema="transparent" onClick={onClick}>
									<FontAwesomeIcon title={t('EDIT')} icon={faEdit} />
								</Button>
							)}
						</DialogWrapper>
						<DialogWrapper
							dialog={(opened, onClose) =>
								opened && (
									<TechnicalRemoveModal
										technicalColumn={params.row}
										onRemove={handleSave}
										onClose={onClose}
									/>
								)
							}
						>
							{(onClick) => (
								<Button size="sm" schema="transparent" onClick={onClick}>
									<FontAwesomeIcon
										title={t('REMOVE')}
										color="#d00"
										icon={faTimes}
									/>
								</Button>
							)}
						</DialogWrapper>
					</Flex>
				),
			},
		]
	}

export const CheckboxSimpleTable = styled(CheckBoxFormField)`
	flex: 0;
	margin: 0;
`
